import { sumGap } from "@patient-watch/forms/pageValidation";
import type { Control } from "react-hook-form";

import { useFormPageCopy } from "@/features/i18n/formPageCopy";
import { cn } from "@/lib/utils";
import { useGroupSum } from "../hooks/useGroupSum";

interface Props {
    inputIds: string[];
    target: number;
    control: Control<Record<string, unknown>>;
}

/**
 * Stays in view while the patient fills a group of counts that must add up to a fixed number:
 * "4 of 10 placed - 6 left", then "12 of 10 placed - 2 over", then "All 10 placed" when it is exact.
 * Over is amber, not red: it is not an error until the patient presses Next.
 */
const SumRemainingBar = ({ inputIds, target, control }: Props) => {
    const copy = useFormPageCopy();
    const total = useGroupSum(inputIds, target, control);
    const { left, over } = sumGap(total, target);
    const state = over > 0 ? "over" : left > 0 ? "left" : "complete";

    return (
        <output
            aria-live="polite"
            data-testid="sum-remaining-bar"
            data-state={state}
            className={cn(
                "sticky bottom-2 z-10 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 border-2 rounded-lg px-3 py-2 font-medium text-lg shadow-sm",
                state === "left" &&
                    "border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100",
                state === "over" &&
                    "border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-500 dark:bg-amber-950 dark:text-amber-100",
                state === "complete" &&
                    "border-green-600 bg-green-50 text-green-900 dark:border-green-500 dark:bg-green-950 dark:text-green-100"
            )}
        >
            {state === "complete" ? (
                <span>{copy.groupComplete(target)}</span>
            ) : (
                <>
                    <span>{copy.groupPlaced(total, target)}</span>
                    <span className="font-semibold">
                        {state === "over" ? copy.groupOver(over) : copy.groupLeft(left)}
                    </span>
                </>
            )}
        </output>
    );
};

export default SumRemainingBar;
